import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, Mail, Lock, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Logo } from './Logo';
import { requestPasswordReset, verifyResetCode, resetPassword, validatePassword } from '../utils/auth';
interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialEmail?: string;
}
type Step = 'email' | 'code' | 'newPassword' | 'success';
export function ForgotPasswordModal({
  isOpen,
  onClose,
  onSuccess,
  initialEmail = ''
}: ForgotPasswordModalProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for back
  const codeInputs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (isOpen) {
      setStep('email');
      setEmail(initialEmail);
      setCode(['', '', '', '', '', '']);
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setDirection(1);
    }
  }, [isOpen, initialEmail]);
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    setError('');
    try {
      await requestPasswordReset(email);
      setDirection(1);
      setStep('code');
    } catch (err) {
      setError('Failed to send reset code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6).split('');
      const newCode = [...code];
      pastedCode.forEach((digit, i) => {
        if (index + i < 6) newCode[index + i] = digit;
      });
      setCode(newCode);
      // Focus last filled input or next empty
      const nextIndex = Math.min(index + pastedCode.length, 5);
      codeInputs.current[nextIndex]?.focus();
      return;
    }
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      codeInputs.current[index + 1]?.focus();
    }
  };
  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputs.current[index - 1]?.focus();
    }
  };
  const handleCodeSubmit = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const result = await verifyResetCode(email, fullCode);
      if (result.success && result.resetToken) {
        setResetToken(result.resetToken);
        setDirection(1);
        setStep('newPassword');
      } else {
        setError(result.error || 'Invalid code');
      }
    } catch (err) {
      setError('Verification failed');
    } finally {
      setIsLoading(false);
    }
  };
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await resetPassword(resetToken, newPassword);
      if (result.success) {
        setDirection(1);
        setStep('success');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Failed to reset password');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  const goBack = () => {
    setDirection(-1);
    if (step === 'code') setStep('email');
    if (step === 'newPassword') setStep('code');
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0
    })
  };
  return <AnimatePresence data-id="element-291">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-id="element-292" />

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 p-6 pb-8 max-h-[90vh] overflow-y-auto" data-id="element-293">
            {/* Header */}
            <div className="flex items-center justify-between mb-6" data-id="element-294">
              {step !== 'email' && step !== 'success' ? <button onClick={goBack} className="p-2 -ml-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-295">
                    <ArrowLeft size={24} className="text-charcoal" data-id="element-296" />
                  </button> : <div className="w-10" data-id="element-297" /> // Spacer
          }
              <h2 className="text-lg font-bold text-charcoal" data-id="element-298">
                Reset Password
              </h2>
              <button onClick={onClose} className="p-2 -mr-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-299">
                <X size={24} className="text-charcoal" data-id="element-300" />
              </button>
            </div>

            <div className="min-h-[300px] relative overflow-hidden" data-id="element-301">
              <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-302">
                {/* Step 1: Email */}
                {step === 'email' && <motion.div key="email" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="absolute inset-0" data-id="element-303">
                    <div className="text-center mb-8" data-id="element-304">
                      <div className="w-16 h-16 bg-sky-blue/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-305">
                        <Mail size={32} className="text-sky-blue" data-id="element-306" />
                      </div>
                      <p className="text-moss-gray" data-id="element-307">
                        Enter your email address and we'll send you a code to
                        reset your password.
                      </p>
                    </div>

                    <form onSubmit={handleEmailSubmit} className="space-y-6" data-id="element-308">
                      <Input label="Email Address" type="email" value={email} onChange={setEmail} placeholder="name@example.com" error={error} data-id="element-309" />
                      <Button type="submit" variant="primary" fullWidth size="lg" loading={isLoading} disabled={!email || isLoading} data-id="element-310">
                        Send Reset Code
                      </Button>
                    </form>
                  </motion.div>}

                {/* Step 2: Code */}
                {step === 'code' && <motion.div key="code" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="absolute inset-0" data-id="element-311">
                    <div className="text-center mb-8" data-id="element-312">
                      <p className="text-charcoal font-medium mb-2" data-id="element-313">
                        Check your email
                      </p>
                      <p className="text-sm text-moss-gray" data-id="element-314">
                        We sent a 6-digit code to{' '}
                        <span className="font-semibold text-charcoal" data-id="element-315">
                          {email}
                        </span>
                      </p>
                    </div>

                    <div className="space-y-6" data-id="element-316">
                      <div className="flex justify-between gap-2" data-id="element-317">
                        {code.map((digit, idx) => <input key={idx} ref={el => codeInputs.current[idx] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleCodeChange(idx, e.target.value)} onKeyDown={e => handleCodeKeyDown(idx, e)} className="w-12 h-14 text-center text-2xl font-bold bg-sand/20 rounded-ios border-2 border-transparent focus:border-dark-green focus:outline-none transition-colors" data-id="element-318" />)}
                      </div>

                      {error && <div className="flex items-center justify-center gap-2 text-red-600" data-id="element-319">
                          <AlertCircle size={16} data-id="element-320" />
                          <span className="text-sm" data-id="element-321">{error}</span>
                        </div>}

                      <Button variant="primary" fullWidth size="lg" loading={isLoading} disabled={code.some(d => !d) || isLoading} onClick={handleCodeSubmit} data-id="element-322">
                        Verify Code
                      </Button>

                      <div className="text-center" data-id="element-323">
                        <button onClick={handleEmailSubmit} disabled={isLoading} className="text-sm text-dark-green font-medium hover:underline flex items-center justify-center gap-2 mx-auto" data-id="element-324">
                          <RefreshCw size={14} data-id="element-325" />
                          Resend Code
                        </button>
                      </div>
                    </div>
                  </motion.div>}

                {/* Step 3: New Password */}
                {step === 'newPassword' && <motion.div key="newPassword" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="absolute inset-0" data-id="element-326">
                    <div className="text-center mb-6" data-id="element-327">
                      <div className="w-16 h-16 bg-dark-green/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-328">
                        <Lock size={32} className="text-dark-green" data-id="element-329" />
                      </div>
                      <h3 className="text-lg font-semibold text-charcoal" data-id="element-330">
                        Create New Password
                      </h3>
                    </div>

                    <form onSubmit={handlePasswordSubmit} className="space-y-4" data-id="element-331">
                      <Input label="New Password" type="password" value={newPassword} onChange={setNewPassword} placeholder="At least 8 characters" data-id="element-332" />
                      <Input label="Confirm Password" type="password" value={confirmPassword} onChange={setConfirmPassword} placeholder="Re-enter password" error={error} data-id="element-333" />

                      <div className="pt-4" data-id="element-334">
                        <Button type="submit" variant="primary" fullWidth size="lg" loading={isLoading} disabled={!newPassword || !confirmPassword || isLoading} data-id="element-335">
                          Reset Password
                        </Button>
                      </div>
                    </form>
                  </motion.div>}

                {/* Step 4: Success */}
                {step === 'success' && <motion.div key="success" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="absolute inset-0 flex flex-col items-center justify-center text-center" data-id="element-336">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                damping: 15,
                stiffness: 200,
                delay: 0.1
              }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-337">
                      <CheckCircle size={40} className="text-green-600" data-id="element-338" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-339">
                      Password Reset!
                    </h3>
                    <p className="text-moss-gray" data-id="element-340">
                      Your password has been successfully updated. You can now
                      log in with your new password.
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}